import {
  challengePublicTitle,
  mapIdToCalendarDate,
  mapIdToVariant,
  mapOgUrl,
  type MapVariant,
} from "@dailywars/core/engine";
import { toUtcDateString } from "@dailywars/core/entitlements/mapAccess";

const APP_NAME = "Daily Wars";
const SEPARATOR = " | ";
export const SITE_ORIGIN = "https://playdailywars.com";
const OG_IMAGE_URL = `${SITE_ORIGIN}/og-image.png`;
const OG_IMAGE_WIDTH = "1200";
const OG_IMAGE_HEIGHT = "630";
const OG_LOCALE = "en_US";
const TWITTER_SITE = "@PlayDailyWars";
const DEFAULT_DESCRIPTION =
  "Your daily tactical puzzle. Build an army and destroy the AI as fast as you can. New maps every day at midnight UTC.";
const DEFAULT_IMAGE_ALT =
  "Daily Wars: daily turn-based strategy versus AI, with today's map and a Play Free button.";

type JsonLd = Record<string, unknown>;

type DocumentHeadOptions = {
  description?: string;
  canonicalPath?: string;
  imageUrl?: string;
  imageAlt?: string;
  robots?: string;
  jsonLd?: JsonLd;
};

function documentTitle(page: string): string {
  return `${APP_NAME}${SEPARATOR}${page}`;
}

export const DOCUMENT_TITLES = {
  home: documentTitle("Daily turn-based tactical game"),
  codex: documentTitle("Game Guide"),
  tutorial: documentTitle("Tutorial"),
  profile: documentTitle("Profile"),
  achievements: documentTitle("Achievements"),
  archive: documentTitle("Archive"),
  stats: documentTitle("Global Stats"),
  hiscores: documentTitle("Hiscores"),
  terms: documentTitle("Terms of Service"),
  privacyPolicy: documentTitle("Privacy Policy"),
  cookiePolicy: documentTitle("Cookie Policy"),
  premium: documentTitle("Upgrade to Premium"),
  updates: documentTitle("Updates"),
  replay: documentTitle("Replay"),
} as const;

export function homeJsonLd(): JsonLd {
  const organizationId = `${SITE_ORIGIN}/#organization`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": organizationId,
        name: APP_NAME,
        url: SITE_ORIGIN,
        description: DEFAULT_DESCRIPTION,
        logo: `${SITE_ORIGIN}/apple-touch-icon.png`,
        sameAs: [
          "https://x.com/PlayDailyWars",
          "https://bsky.app/profile/playdailywars.bsky.social",
          "https://facebook.com/playdailywars",
          "https://www.linkedin.com/company/playdailywars/",
        ],
      },
      {
        "@type": ["VideoGame", "WebApplication"],
        name: APP_NAME,
        url: SITE_ORIGIN,
        description: DEFAULT_DESCRIPTION,
        image: OG_IMAGE_URL,
        applicationCategory: "GameApplication",
        operatingSystem: "Web browser",
        publisher: { "@id": organizationId },
        offers: {
          "@type": "Offer",
          price: 0,
          priceCurrency: "USD",
        },
      },
    ],
  };
}

function absoluteUrl(path: string): string {
  if (path === "/") return SITE_ORIGIN;
  return `${SITE_ORIGIN}${path}`;
}

function documentHeadMeta({
  title,
  description,
  imageUrl,
  imageAlt,
}: {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
}) {
  return [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:site_name", content: APP_NAME },
    { property: "og:description", content: description },
    { property: "og:image", content: imageUrl },
    { property: "og:image:width", content: OG_IMAGE_WIDTH },
    { property: "og:image:height", content: OG_IMAGE_HEIGHT },
    { property: "og:image:alt", content: imageAlt },
    { property: "og:type", content: "website" },
    { property: "og:locale", content: OG_LOCALE },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: TWITTER_SITE },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: imageUrl },
    { name: "twitter:image:alt", content: imageAlt },
  ];
}

export function documentHead(title: string, options: DocumentHeadOptions = {}) {
  const description = options.description ?? DEFAULT_DESCRIPTION;
  const imageUrl = options.imageUrl ?? OG_IMAGE_URL;
  const imageAlt = options.imageAlt ?? DEFAULT_IMAGE_ALT;
  const meta = documentHeadMeta({ title, description, imageUrl, imageAlt });

  if (options.robots) {
    meta.push({ name: "robots", content: options.robots });
  }

  const links: Array<{ rel: string; href: string }> = [];
  if (options.canonicalPath) {
    const canonicalUrl = absoluteUrl(options.canonicalPath);
    meta.push({ property: "og:url", content: canonicalUrl });
    links.push({ rel: "canonical", href: canonicalUrl });
  }

  const scripts = options.jsonLd
    ? [{ type: "application/ld+json", children: JSON.stringify(options.jsonLd) }]
    : undefined;

  return {
    meta,
    ...(links.length > 0 ? { links } : {}),
    ...(scripts ? { scripts } : {}),
  };
}

function mapVariantLabel(variant: MapVariant): string {
  return variant === "premium" ? "Premium" : "Standard";
}

export function playMapTitle(mapId: string): string {
  const mapDate = mapIdToCalendarDate(mapId);
  const variant = mapVariantLabel(mapIdToVariant(mapId));
  return documentTitle(`${mapDate} ${variant}`);
}

export function playMapDescription(mapId: string): string {
  const mapDate = mapIdToCalendarDate(mapId);
  const variant = mapVariantLabel(mapIdToVariant(mapId)).toLowerCase();
  return `Play the Daily Wars ${variant} map for ${mapDate}. Turn-based tactical skirmish against the AI.`;
}

export function playMapRobots(mapId: string, now = new Date()): string | undefined {
  if (mapIdToCalendarDate(mapId) === toUtcDateString(now)) {
    return undefined;
  }
  return "noindex, follow";
}

export function betaMapTitle(slug: string): string {
  return documentTitle(`Beta map ${slug}`);
}

export function betaMapDescription(slug: string): string {
  return `Beta map ${slug}. Direct-link play for draft maps.`;
}

export function betaMapRobots(): string {
  return "noindex, nofollow";
}

export function challengeMapTitle(challengeNumber: number): string {
  return documentTitle(challengePublicTitle(challengeNumber));
}

export function challengeMapDescription(challengeNumber: number): string {
  return `${challengePublicTitle(challengeNumber)}. Premium-only weekly challenge: one try, no full-turn undo, no restart.`;
}

export function challengeLeaderboardTitle(challengeNumber: number): string {
  return documentTitle(`Leaderboard, ${challengePublicTitle(challengeNumber)}`);
}

export function challengeLeaderboardDescription(challengeNumber: number): string {
  return `See who cleared ${challengePublicTitle(challengeNumber)} in the fewest days.`;
}

export function leaderboardMapTitle(mapId: string): string {
  const mapDate = mapIdToCalendarDate(mapId);
  const variant = mapVariantLabel(mapIdToVariant(mapId));
  return documentTitle(`Leaderboard, ${mapDate} ${variant}`);
}

export function leaderboardMapDescription(mapId: string): string {
  const mapDate = mapIdToCalendarDate(mapId);
  const variant = mapVariantLabel(mapIdToVariant(mapId)).toLowerCase();
  return `See who cleared the ${mapDate} ${variant} map in the fewest days.`;
}

export function mapOgImageUrl(mapId: string): string {
  return `${SITE_ORIGIN}${mapOgUrl(mapId)}`;
}

function formatUtcMonthLabel(monthKey: string): string {
  const year = Number(monthKey.slice(0, 4));
  const monthIndex = Number(monthKey.slice(5, 7)) - 1;
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, monthIndex, 1)));
}

export function hiscoresTitle(monthKey?: string, scope?: "challenge"): string {
  if (scope === "challenge") return documentTitle("Hiscores, Challenge");
  if (!monthKey) return DOCUMENT_TITLES.hiscores;
  return documentTitle(`Hiscores, ${formatUtcMonthLabel(monthKey)}`);
}

export function hiscoresDescription(monthKey?: string, scope?: "challenge"): string {
  if (scope === "challenge") {
    return "All-time challenge star standings for Daily Wars.";
  }
  if (!monthKey) return "All-time and monthly medal standings for Daily Wars.";
  return `Medal standings for ${formatUtcMonthLabel(monthKey)} in Daily Wars.`;
}

export function hiscoresCanonicalPath(monthKey?: string, scope?: "challenge"): string {
  if (scope === "challenge") return "/hiscores?scope=challenge";
  return monthKey ? `/hiscores?month=${monthKey}` : "/hiscores";
}
